import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, CheckCircle2, AlertCircle, Clock } from "lucide-react";

/**
 * Sprint 29.1 — Owner-side widget showing per-employee task progress
 * for the day. Names, roles and last activities are all resolved
 * dynamically from `/api/dashboard/staff-status`.
 */

function timeShort(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  } catch { return ""; }
}

const ROLE_LABEL = {
  staff:   { tr: "Personel",  de: "Mitarbeiter" },
  waiter:  { tr: "Servis",    de: "Service" },
  kitchen: { tr: "Mutfak",    de: "Küche" },
};

export default function StaffStatusWidget() {
  const { lang } = useI18n();
  const de = lang === "de";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/dashboard/staff-status");
        if (alive) { setData(data); setErr(null); }
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "load failed");
      }
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (err) return null;      // fail silently — this is an ancillary widget
  if (!data) {
    return <div className="h-40 rounded-xl border bg-muted/30 animate-pulse" data-testid="staff-status-loading" />;
  }

  const employees = data.employees || [];
  const activeEmployees = employees.filter(e => e.is_active_today);
  const closingDone = !!data.daily_closing_done;

  return (
    <Card className="border rounded-xl shadow-elev-1" data-testid="staff-status-widget">
      <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-base font-display font-semibold tracking-tight flex items-center gap-2">
            <Users className="w-4 h-4" />
            {de ? "Personel Bugünkü Durum" : "Heutiger Personalstatus"}
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5">
            {activeEmployees.length}
            {" "}
            {de ? "aktiv heute" : "bugün aktif"}
          </p>
        </div>
        <Badge
          variant="outline"
          className={closingDone
            ? "bg-emerald-50 text-emerald-700 border-emerald-200 rounded-md"
            : "bg-rose-50 text-rose-700 border-rose-200 rounded-md"}
          data-testid="staff-status-closing-badge"
        >
          {closingDone ? (
            <><CheckCircle2 className="w-3 h-3 mr-1" />{de ? "Tagesabschluss ✓" : "Gün Sonu ✓"}</>
          ) : (
            <><AlertCircle className="w-3 h-3 mr-1" />{de ? "Tagesabschluss offen" : "Gün Sonu Bekliyor"}</>
          )}
        </Badge>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        {employees.length === 0 && (
          <div className="text-sm text-muted-foreground py-3" data-testid="staff-status-empty">
            {de ? "Noch kein Personal-Konto angelegt." : "Henüz personel hesabı yok."}
          </div>
        )}
        {employees.map((emp) => {
          const roleLabel = ROLE_LABEL[emp.role]?.[lang] || emp.role;
          const dotClass = emp.is_active_today ? "bg-emerald-500" : "bg-slate-300";
          return (
            <div key={emp.user_id}
                 data-testid={`staff-status-emp-${emp.user_id}`}
                 className="flex items-center gap-3 py-2 border-t first:border-t-0">
              <span className={`w-2.5 h-2.5 rounded-full ${dotClass} ${emp.is_active_today ? "animate-pulse" : ""}`} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate flex items-center gap-2">
                  <span data-testid={`staff-status-name-${emp.user_id}`}>{emp.name}</span>
                  <Badge variant="secondary" className="rounded-md text-[9px] uppercase tracking-wider">
                    {roleLabel}
                  </Badge>
                </div>
                {emp.last_activity_title ? (
                  <div className="text-xs text-muted-foreground truncate flex items-center gap-1 mt-0.5">
                    <Clock className="w-3 h-3" />
                    <span className="font-mono">{timeShort(emp.last_activity_at)}</span>
                    <span className="opacity-40">·</span>
                    <span className="truncate">{emp.last_activity_title}</span>
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {de ? "Heute noch keine Aktivität" : "Bugün henüz etkinlik yok"}
                  </div>
                )}
              </div>
              <div className="shrink-0 text-right">
                <div className="font-mono text-sm font-semibold" data-testid={`staff-status-progress-${emp.user_id}`}>
                  {emp.role_tasks_completed} / {emp.role_tasks_total}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {de ? "Aufgaben" : "Görev"}
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
